import {
  APP_ROUTE_MAP,
  canAccessRouteRule,
  getDefaultRoute,
} from "../config/accessControl";

/** Routes that must never be treated as a restorable "last visited" page. */
const EXCLUDED_RESTORE_PATHS = new Set(["/", "/login"]);

/**
 * Derive a coarse module key from a pathname (e.g. /view-entry-data → view-entry-data).
 */
export const deriveModuleFromPath = (pathname = "") => {
  if (!pathname || pathname === "/") {
    return null;
  }
  const cleaned = pathname.startsWith("/") ? pathname.slice(1) : pathname;
  if (!cleaned) {
    return null;
  }
  const slash = cleaned.indexOf("/");
  return slash >= 0 ? cleaned.slice(0, slash) : cleaned;
};

/**
 * Build the route string to persist (pathname + search to keep Phase 1 URL state).
 */
export const buildPersistableRoute = (pathname = "", search = "") => {
  if (!pathname || EXCLUDED_RESTORE_PATHS.has(pathname)) {
    return null;
  }
  return `${pathname}${search || ""}`;
};

/**
 * Whether a location change is meaningful enough to persist server-side.
 */
export const isMeaningfulRoute = (pathname = "") => {
  if (!pathname || EXCLUDED_RESTORE_PATHS.has(pathname)) {
    return false;
  }
  // Persist any authenticated app path (including nested paths without APP_ROUTE_MAP).
  return pathname.startsWith("/");
};

/**
 * Resolve a saved route against current RBAC. Never blindly navigate.
 *
 * @param {string|null|undefined} savedRoute path or path+query
 * @param {object} access { permissions, selectedPrivilegeGroups, roleName }
 * @returns {string} safe route (path+query) or default route
 */
export const resolveRestorableRoute = (savedRoute, access = {}) => {
  const fallback = getDefaultRoute(access);

  if (!savedRoute || typeof savedRoute !== "string") {
    return fallback;
  }

  let pathname = savedRoute;
  let search = "";
  try {
    // Support path+query without needing absolute URL parsing.
    const qIndex = savedRoute.indexOf("?");
    if (qIndex >= 0) {
      pathname = savedRoute.slice(0, qIndex);
      search = savedRoute.slice(qIndex);
    }
  } catch {
    return fallback;
  }

  if (!pathname || EXCLUDED_RESTORE_PATHS.has(pathname)) {
    return fallback;
  }

  if (!pathname.startsWith("/") || pathname.startsWith("//") || pathname.includes("://")) {
    return fallback;
  }

  const routeRule = APP_ROUTE_MAP[pathname];

  // Unknown / removed routes with no rule: only allow if they look like nested
  // children of a known parent that the user can access.
  if (!routeRule) {
    const parentPath = Object.keys(APP_ROUTE_MAP)
      .filter((p) => p !== "/" && p !== "/login")
      .sort((a, b) => b.length - a.length)
      .find((p) => pathname === p || pathname.startsWith(`${p}/`));

    if (!parentPath) {
      return fallback;
    }

    const parentRule = APP_ROUTE_MAP[parentPath];
    if (!canAccessRouteRule(parentRule, access)) {
      return fallback;
    }

    return `${pathname}${search}`;
  }

  if (routeRule.publicOnly || routeRule.isFullyPublic) {
    return fallback;
  }

  if (!canAccessRouteRule(routeRule, access)) {
    return fallback;
  }

  return `${pathname}${search}`;
};

const userAppStateUtils = {
  deriveModuleFromPath,
  buildPersistableRoute,
  isMeaningfulRoute,
  resolveRestorableRoute,
};

export default userAppStateUtils;
